import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../firebase';

const AuthContext = createContext(null);

function requireFirebase() {
  if (!auth) {
    throw new Error('Firebase is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return undefined;
    }

    return onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      isFirebaseConfigured,
      loading,
      signup(email, password) {
        requireFirebase();
        return createUserWithEmailAndPassword(auth, email, password);
      },
      login(email, password) {
        requireFirebase();
        return signInWithEmailAndPassword(auth, email, password);
      },
      logout() {
        requireFirebase();
        return signOut(auth);
      },
      resetPassword(email) {
        requireFirebase();
        return sendPasswordResetEmail(auth, email);
      },
    }),
    [currentUser, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
